// Clone Stamp tool (S) — samples pixels from a source area and paints them
// onto a target area. Alt+click sets the source point; then paint normally.
// Supports multi-layer sampling: Current, Current & Below, All Layers.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { mergeBBox, stampLine } from "../pixel/brush";
import type { Layer, RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { KeyModifiers, PointerSample, Tool, ToolContext } from "./Tool";

export interface BrushSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
}

export class CloneStampTool implements Tool {
  readonly id = "clone-stamp";
  readonly shortcut = "S";

  /** Brush settings for the clone stamp. Mirrors BrushTool's interface. */
  settings: BrushSettings = { size: 30, hardness: 0.5, opacity: 1, flow: 1 };
  /**
   * Options-bar "Mode" blend mode (Normal/Dissolve/Behind/Clear — issue #81).
   * Not yet consumed by the stamp compositor below, which always does a
   * plain alpha-over blend; storing it here at least makes the options-bar
   * control functional (read/write) instead of a dead no-op select.
   */
  blendMode = "normal";
  /** Whether the source point moves relative to the brush (Photoshop default). */
  aligned = true;
  /** Which layers to sample from. */
  sampleMode: "current" | "below" | "all" = "current";

  private sourcePoint: { x: number; y: number } | null = null;
  private lockedOffsetX = 0;
  private lockedOffsetY = 0;
  private locked = false;
  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    sourceSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    offsetX: number;
    offsetY: number;
    selMask: { mask: Uint8Array | undefined; w: number | undefined };
    sourceW: number;
    sourceH: number;
  } | null = null;

  /** Get the current source point for overlay rendering. */
  getSourcePoint(): { x: number; y: number } | null {
    return this.sourcePoint;
  }

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): void {
    // Alt+click sets source — handled in onPointerDown.
  }

  /** Extract selection mask from context. */
  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  /** Composite visible layers at a doc-space point for multi-layer sampling. */
  private compositeSourceAt(ctx: ToolContext, docX: number, docY: number, radius: number): { data: Uint8ClampedArray; w: number; h: number; ox: number; oy: number } {
    const doc = ctx.doc!;
    const size = Math.ceil(radius) * 2 + 2;
    const ox = Math.floor(docX - radius) - 1;
    const oy = Math.floor(docY - radius) - 1;
    const data = new Uint8ClampedArray(size * size * 4);

    const layersToSample = this.sampleMode === "all"
      ? doc.layers
      : this.sampleMode === "below"
        ? this.layersBelowActive(doc)
        : [];

    for (const layer of layersToSample) {
      if (layer.kind !== "raster" || !layer.visible || layer.opacity <= 0) continue;
      const raster = layer as RasterLayer;
      const src = raster.pixels.data;
      const sw = raster.pixels.width;
      const sh = raster.pixels.height;

      const sx0 = Math.max(0, ox - raster.x);
      const sy0 = Math.max(0, oy - raster.y);
      const sx1 = Math.min(sw, ox + size - raster.x);
      const sy1 = Math.min(sh, oy + size - raster.y);

      for (let sy = sy0; sy < sy1; sy++) {
        for (let sx = sx0; sx < sx1; sx++) {
          const dx = sx + raster.x - ox;
          const dy = sy + raster.y - oy;
          if (dx < 0 || dy < 0 || dx >= size || dy >= size) continue;
          const si = (sy * sw + sx) * 4;
          const di = (dy * size + dx) * 4;
          const sa = src[si + 3]! / 255 * layer.opacity;
          const ia = 1 - sa;
          data[di] = Math.round(data[di]! * ia + src[si]! * sa);
          data[di + 1] = Math.round(data[di + 1]! * ia + src[si + 1]! * sa);
          data[di + 2] = Math.round(data[di + 2]! * ia + src[si + 2]! * sa);
          data[di + 3] = Math.min(255, Math.round(data[di + 3]! * ia + src[si + 3]! * sa));
        }
      }
    }

    return { data, w: size, h: size, ox, oy };
  }

  private layersBelowActive(doc: import("../document/Document").Document): Layer[] {
    const activeIdx = this.findLayerIndex(doc.layers, doc.activeLayerId);
    if (activeIdx === null) return [];
    return doc.layers.slice(0, activeIdx);
  }

  private findLayerIndex(layers: readonly Layer[], id: string | null): number | null {
    if (!id) return null;
    for (let i = 0; i < layers.length; i++) {
      if (layers[i]!.id === id) return i;
      if (layers[i]!.kind === "group") {
        const inner = this.findLayerIndex((layers[i]! as import("../document/Layer").GroupLayer).children, id);
        if (inner !== null) return i;
      }
    }
    return null;
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;

    // Alt+click sets the source point
    if (s.altKey) {
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      this.sourcePoint = { x: p.x, y: p.y };
      this.locked = false;
      return;
    }

    if (!this.sourcePoint) return;
    if (!ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    // Aligned mode: lock offset once at first stroke, preserve across strokes.
    // Non-aligned: reset offset to original Alt+click position each stroke.
    let offsetX: number;
    let offsetY: number;
    if (this.aligned) {
      if (!this.locked) {
        this.lockedOffsetX = this.sourcePoint.x - p.x;
        this.lockedOffsetY = this.sourcePoint.y - p.y;
        this.locked = true;
      }
      offsetX = this.lockedOffsetX;
      offsetY = this.lockedOffsetY;
    } else {
      offsetX = this.sourcePoint.x - p.x;
      offsetY = this.sourcePoint.y - p.y;
    }

    // For multi-layer sampling, pre-composite the source region
    let sourceSnapshot: Uint8ClampedArray;
    let sourceW = layer.pixels.width;
    let sourceH = layer.pixels.height;

    if (this.sampleMode === "current") {
      sourceSnapshot = new Uint8ClampedArray(layer.pixels.data);
    } else {
      const radius = this.settings.size / 2;
      const srcDocX = this.sourcePoint.x;
      const srcDocY = this.sourcePoint.y;
      const composite = this.compositeSourceAt(ctx, srcDocX, srcDocY, radius + Math.max(Math.abs(offsetX), Math.abs(offsetY)) + 10);
      sourceSnapshot = composite.data;
      sourceW = composite.w;
      sourceH = composite.h;
    }

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      sourceSnapshot,
      lastX: docX,
      lastY: docY,
      bbox: null,
      offsetX,
      offsetY,
      selMask: this.getSelMask(ctx),
      sourceW,
      sourceH,
    };

    this.stamp(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    if (s.altKey) {
      // Moving while holding alt repositions source
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      this.sourcePoint = { x: p.x, y: p.y };
      this.locked = false;
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    // Stamp along the line from last position
    const step = 2;
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.stamp(ctx, cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private stamp(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active || !this.sourcePoint) return;
    const srcDocX = docX + this.active.offsetX;
    const srcDocY = docY + this.active.offsetY;
    const dstLayer = this.active.layer;
    const radius = this.settings.size / 2;
    const hardness = this.settings.hardness;
    const opacity = this.settings.flow;

    const srcData = this.active.sourceSnapshot;
    const srcW = this.active.sourceW;
    const srcH = this.active.sourceH;

    const dstData = dstLayer.pixels.data;
    const w = dstLayer.pixels.width;
    const h = dstLayer.pixels.height;
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));
    const { mask: selMask, w: selMaskW } = this.active.selMask;
    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    // For multi-layer mode, source coords are relative to the composite snapshot
    const srcSnapshotOx = this.sampleMode !== "current"
      ? Math.floor(this.sourcePoint.x - radius) - 1 - this.active.sourceW / 2 + radius
      : 0;
    const srcSnapshotOy = this.sampleMode !== "current"
      ? Math.floor(this.sourcePoint.y - radius) - 1 - this.active.sourceH / 2 + radius
      : 0;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;

        // Selection masking
        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          if (selVal === 0) continue;
        }

        // Map to source position
        let sx: number, sy: number;
        if (this.sampleMode === "current") {
          sx = Math.round(srcDocX + (px - docX));
          sy = Math.round(srcDocY + (py - docY));
        } else {
          sx = Math.round(srcDocX - srcSnapshotOx + (px - docX));
          sy = Math.round(srcDocY - srcSnapshotOy + (py - docY));
        }
        if (sx < 0 || sx >= srcW || sy < 0 || sy >= srcH) continue;
        const dstIdx = (py * w + px) * 4;
        const srcIdx = (sy * srcW + sx) * 4;
        // Soft-edge falloff + opacity.
        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const alpha = dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1;
        let blend = alpha * opacity;

        // Apply selection alpha
        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          blend *= selVal / 255;
        }

        // Lock-transparency: skip pixels that are fully transparent.
        if (dstLayer.lockTransparency) {
          if (dstData[dstIdx + 3] === 0) continue;
        }

        dstData[dstIdx] = Math.round(dstData[dstIdx]! * (1 - blend) + srcData[srcIdx]! * blend);
        dstData[dstIdx + 1] = Math.round(dstData[dstIdx + 1]! * (1 - blend) + srcData[srcIdx + 1]! * blend);
        dstData[dstIdx + 2] = Math.round(dstData[dstIdx + 2]! * (1 - blend) + srcData[srcIdx + 2]! * blend);
        dstData[dstIdx + 3] = Math.round(dstData[dstIdx + 3]! * (1 - blend) + srcData[srcIdx + 3]! * blend);
        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    dstLayer.pixels.markDirty();
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
